/**
 * Worker tick frame → renderer-shaped row arrays.
 *
 * The worker emits per-tick row-shaped JSON (see `wargame-view-rows`) that
 * mirrors the legacy `my_*` SpacetimeDB views. This bridge caches the latest
 * frame and produces the same arrays/maps that the `_get*` methods in
 * `spacetimedb-network.ts` previously assembled from `this.conn.db.*`.
 *
 * Field names in the JSON arrive snake_case (plain serde), so the bridge
 * reads `row.unit_id`/`row.army_index`/etc. directly — unlike the SpacetimeDB
 * Rust→TS bindings which lower-camel-case row fields.
 */

import type { WorkerTickFrame } from "./worker-ws";

export interface VisibilityBitmapShape {
  cell_size: number;
  cols: number;
  rows: number;
  bits: Uint8Array;
}

export class WorkerTickBridge {
  private _matchId: string | null = null;
  private _frame: WorkerTickFrame | null = null;
  /** Army index that owns the frames on this bridge. Locked in from the
   *  first frame whose friendly units carry an army_index; subsequent
   *  frames whose units belong to a different army are dropped (this is
   *  how we defend against the host's per-player loop or the relay ever
   *  delivering the OTHER player's frame to us, which would show as a
   *  tick-rate "view swap" between the two armies). */
  private _lockedArmyIndex: number | null = null;
  /** Highest tick we have accepted; later frames must not regress. */
  private _highWaterTick: number = -1;

  /** Bind the bridge to a specific match id; clears any cached frame. */
  attach(matchId: string): void {
    if (this._matchId === matchId) return;
    this._matchId = matchId;
    this._frame = null;
    this._lockedArmyIndex = null;
    this._highWaterTick = -1;
  }

  /** Detach (worker disconnected, match ended, etc.). */
  detach(): void {
    this._matchId = null;
    this._frame = null;
    this._lockedArmyIndex = null;
    this._highWaterTick = -1;
  }

  /** True when this bridge is the source of truth for `matchId`. */
  isActive(matchId: string | null): boolean {
    return !!matchId && this._matchId === matchId && this._frame !== null;
  }

  /** Stash the latest tick frame; returns true if accepted. */
  setFrame(frame: WorkerTickFrame): boolean {
    const units = Array.isArray((frame as any)?.units) ? (frame as any).units : [];
    // Determine which army this frame belongs to from its friendly units.
    let frameArmy: number | null = null;
    for (const u of units) {
      const ai = u?.army_index;
      if (typeof ai === "number") {
        frameArmy = ai;
        break;
      }
    }
    if (frameArmy != null) {
      if (this._lockedArmyIndex == null) {
        // First frame with a discernible army wins; lock it in.
        this._lockedArmyIndex = frameArmy;
      } else if (frameArmy !== this._lockedArmyIndex) {
        console.warn(
          "[worker-bridge] drop frame: friendly army_index",
          frameArmy,
          "!= locked",
          this._lockedArmyIndex,
          "(tick",
          (frame as any)?.tick,
          ")",
        );
        return false;
      }
    }
    // Reject out-of-order / stale frames.
    const tick = Number((frame as any)?.tick ?? 0);
    if (tick < this._highWaterTick) {
      return false;
    }
    this._highWaterTick = tick;
    this._frame = frame;
    return true;
  }

  /** Latest tick from the cached frame, or 0. */
  get tick(): number {
    return this._frame?.tick ?? 0;
  }

  /** Latest phase string, or empty. */
  get phase(): string {
    return this._frame?.phase ?? "";
  }

  /** Latest events array, or empty. */
  get events(): any[] {
    const evs = this._frame?.events;
    return Array.isArray(evs) ? evs : [];
  }

  // ---------- Per-table accessors mirroring _get* output shapes ----------

  getEngagements(): any[] {
    const arr = this._asArray(this._frame?.engagements);
    return arr.map((row) => ({
      id: row.engagement_id,
      unit_a_id: row.unit_a_id,
      unit_b_id: row.unit_b_id,
      tick_started: row.tick_started,
      ticks_active: row.ticks_active,
      direction_on_a: row.direction_on_a,
      direction_on_b: row.direction_on_b,
      damage_a_to_b: row.damage_a_to_b,
      damage_b_to_a: row.damage_b_to_a,
      morale_a_to_b: row.morale_a_to_b,
      morale_b_to_a: row.morale_b_to_a,
      outcome: row.outcome,
    }));
  }

  getProjectiles(): any[] {
    const arr = this._asArray(this._frame?.projectiles);
    return arr.map((row) => ({
      id: row.projectile_id,
      source_unit_id: row.source_unit_id,
      x: row.x,
      y: row.y,
      vx: row.vx,
      vy: row.vy,
      team_index: row.team_index,
      projectile_type: row.projectile_type,
      progress: row.progress,
    }));
  }

  getMessengers(): any[] {
    const arr = this._asArray(this._frame?.messengers);
    return arr.map((row) => ({
      id: row.messenger_id,
      x: row.x,
      y: row.y,
      target_unit_id: row.target_unit_id,
      order_type: row.order_type,
    }));
  }

  getFriendlyUnits(): any[] {
    const arr = this._asArray(this._frame?.units);
    const engagementsByUnit = this._engagementIdsByUnit();
    const skillsByUnit = this._skillsByUnit();
    const orderQueueByUnit = this._orderQueueByUnit();
    const pathByUnit = this._pathWaypointsByUnit();

    return arr.map((row) => {
      const unit: any = {
        id: row.unit_id,
        army_index: row.army_index,
        archetype: row.archetype,
        name: row.name,
        x: row.x,
        y: row.y,
        facing: row.facing,
        formation_shape: row.formation_shape ?? null,
        length: row.length,
        width: row.width,
        state: row.state,
        stance: row.stance,
        strength: row.strength,
        max_strength: row.max_strength,
        troops_alive: row.troops_alive ?? 0,
        troops_max: row.troops_max ?? 0,
        is_general: row.is_general,
        is_captain: row.is_captain ?? false,
        captain_unit_id: row.captain_unit_id ?? null,
        category: row.category,
        symbol: row.symbol,
        level: row.level,
        engagement_ids: engagementsByUnit.get(row.unit_id) || [],
        is_ranged: row.is_ranged,
        suppressed: row.suppressed,
        follow_mode: row.follow_mode,
        builder_enabled: row.builder_enabled ?? false,
        garrisoned_in: row.garrisoned_in ?? null,
        morale: row.morale,
        max_morale: row.max_morale,
        fatigue: row.fatigue,
        reinforced_sides: row.reinforced_sides,
        chain_front_unit_id: row.chain_front_unit_id ?? null,
        chain_rear_unit_id: row.chain_rear_unit_id ?? null,
        chain_depth: row.chain_depth,
        chain_size: row.chain_size,
        has_pending_order: row.has_pending_order,
        current_order_type: row.current_order_type ?? null,
        order_queue: orderQueueByUnit.get(row.unit_id) || [],
        skills: skillsByUnit.get(row.unit_id) || [],
        experience: row.experience,
        idle_stance: row.idle_stance,
        guard_point:
          row.guard_point_x != null && row.guard_point_y != null
            ? { x: row.guard_point_x, y: row.guard_point_y }
            : null,
        guard_radius: row.guard_radius,
        vision_range: row.vision_range,
      };
      if (row.persistent_id) {
        unit.persistent_id = row.persistent_id;
      }
      if (row.is_ranged) {
        unit.ammo = row.ammo ?? 0;
        unit.max_ammo = row.max_ammo ?? 0;
        unit.fire_range = row.fire_range ?? 0;
        unit.ranged_stance = row.ranged_stance ?? "fire_at_will";
        unit.fire_at_will = row.fire_at_will ?? false;
      }
      if (row.move_target_x != null && row.move_target_y != null) {
        unit.move_target_x = row.move_target_x;
        unit.move_target_y = row.move_target_y;
      }
      if (row.move_target_facing != null) {
        unit.move_target_facing = row.move_target_facing;
      }
      const pathWaypoints = pathByUnit.get(row.unit_id) || [];
      if (pathWaypoints.length > 0) {
        unit.path_waypoints = pathWaypoints;
      }
      return unit;
    });
  }

  getEnemyUnits(): any[] {
    const arr = this._asArray(this._frame?.enemy_units);
    const engagementsByUnit = this._engagementIdsByUnit();
    return arr.map((row) => {
      const unit: any = {
        id: row.unit_id,
        army_index: row.army_index,
        archetype: row.archetype,
        name: row.name,
        x: row.x,
        y: row.y,
        facing: row.facing,
        length: row.length,
        width: row.width,
        state: row.state,
        stance: row.stance,
        strength: row.strength,
        max_strength: row.max_strength,
        troops_alive: row.troops_alive ?? 0,
        troops_max: row.troops_max ?? 0,
        is_general: row.is_general,
        category: row.category,
        symbol: row.symbol,
        level: row.level,
        engagement_ids: engagementsByUnit.get(row.unit_id) || [],
        is_ranged: row.is_ranged,
        suppressed: row.suppressed,
        follow_mode: row.follow_mode,
        garrisoned_in: row.garrisoned_in ?? null,
      };
      if (row.is_ranged) {
        unit.ammo = row.ammo ?? 0;
        unit.max_ammo = row.max_ammo ?? 0;
        unit.fire_range = row.fire_range ?? 0;
        unit.ranged_stance = row.ranged_stance ?? "fire_at_will";
        unit.fire_at_will = row.fire_at_will ?? false;
      }
      return unit;
    });
  }

  getVisibilityBitmap(): VisibilityBitmapShape | null {
    const v = this._frame?.visibility;
    if (!v || typeof v !== "object") return null;
    const cols = (v.cols ?? 0) >>> 0;
    const rows = (v.rows ?? 0) >>> 0;
    const totalBits = cols * rows;
    const packedLen = (totalBits + 7) >>> 3;
    const packed = new Uint8Array(packedLen);
    const rleSrc = v.rle;
    const rle: Uint8Array =
      rleSrc instanceof Uint8Array
        ? rleSrc
        : Array.isArray(rleSrc)
          ? new Uint8Array(rleSrc)
          : new Uint8Array(0);
    let dst = 0;
    for (let i = 0; i < rle.length; i++) {
      const b = rle[i];
      if (b === 0x00) {
        const count = rle[++i] | 0;
        const end = Math.min(dst + count, packedLen);
        dst = end;
      } else {
        if (dst < packedLen) packed[dst++] = b;
      }
    }
    return { cell_size: v.cell_size, cols, rows, bits: packed };
  }

  /** Mirror of `_getMatchMapState`'s output shape, derived from the worker's
   *  per-tick `structure_state` array. */
  getMapState(): {
    gate_open: boolean[];
    structure_hp: number[];
    structures: any[];
    capture_points: any[];
  } {
    const rows = this._asArray(this._frame?.structure_state);
    const captureRows = this._asArray(this._frame?.capture_points);
    const maxIdx = rows.reduce(
      (m: number, r: any) => Math.max(m, (r.structure_index | 0)),
      -1,
    );
    const count = maxIdx + 1;
    const gate_open: boolean[] = Array.from({ length: count }, () => false);
    const structure_hp: number[] = Array.from({ length: count }, () => 0);
    const structures: any[] = new Array(count);
    for (const r of rows) {
      const i = r.structure_index | 0;
      gate_open[i] = !!r.gate_open;
      structure_hp[i] = r.hp ?? 0;
      if (r.structure_type && r.x != null && r.y != null) {
        structures[i] = {
          index: i,
          type: r.structure_type,
          material: r.material ?? null,
          x: r.x,
          y: r.y,
          width: r.width ?? null,
          height: r.height ?? null,
          radius: r.radius ?? null,
          facing: r.facing ?? null,
          owner_team: r.owner_team ?? null,
          hp: r.hp ?? 0,
          max_hp: r.max_hp ?? 0,
          open: !!r.gate_open,
          built_in_match: !!r.built_in_match,
          built_by_army: r.built_by_army ?? null,
          built_tick: r.built_tick ?? null,
          wall_group_id: r.wall_group_id ?? null,
        };
      }
    }
    const capture_points = captureRows
      .map((r: any) => ({
        structure_index: r.structure_index,
        owner: r.owner ?? null,
        progress: r.progress,
        capturing_team: r.capturing_team ?? null,
        hold_counter: r.hold_counter,
      }))
      .sort((l: any, r: any) => l.structure_index - r.structure_index);
    return { gate_open, structure_hp, structures, capture_points };
  }

  /** Wall-build projects in the latest frame, normalized into the same
   *  shape `_getMatchWallBuildProjects` produced from SpacetimeDB. */
  getWallBuildProjects(): any[] {
    const arr = this._asArray(this._frame?.wall_build_projects);
    return arr.map((project: any) => ({
      id: project.id,
      army_index: project.army_index,
      team_index: project.team_index,
      general_unit_id: project.general_unit_id,
      completed_tick: project.completed_tick ?? null,
      sections: Array.isArray(project.sections)
        ? project.sections.map((section: any) => ({
            center_x: section.center_x,
            center_y: section.center_y,
            facing: section.facing,
            length: section.length,
            width: section.width,
            progress: section.progress,
            completed: !!section.completed,
            structure_index: section.structure_index ?? null,
          }))
        : [],
    }));
  }

      getCamps(): any[] {
        return this._asArray(this._frame?.camps);
      }

  getHarvestTiles(): any[] {
    const arr = this._asArray(this._frame?.harvest_tiles);
    return arr.map((tile: any) => ({
      army_index: tile.army_index | 0,
      tile_x: tile.tile_x | 0,
      tile_y: tile.tile_y | 0,
      wood_remaining: tile.wood_remaining | 0,
    }));
  }

  getAntGroups(): any[] {
    const arr = this._asArray(this._frame?.ant_groups);
    return arr;
  }

  getWoodStockpile(): Record<number, number> {
    const raw = this._frame?.wood_stockpile;
    if (!raw || typeof raw !== "object") return {};
    const out: Record<number, number> = {};
    for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
      const idx = Number(k);
      if (!Number.isFinite(idx)) continue;
      out[idx] = Number(v) | 0;
    }
    return out;
  }

  getTerrainOverrides(): Record<string, number> {
    const raw = this._frame?.terrain_overrides;
    if (!raw || typeof raw !== "object") return {};
    return raw as Record<string, number>;
  }

  getWatchtowers(): any[] {
    return this._asArray(this._frame?.watchtowers);
  }

  // ---------- internal helpers ----------

  private _asArray(v: any): any[] {
    return Array.isArray(v) ? v : [];
  }

  private _engagementIdsByUnit(): Map<string, string[]> {
    const out = new Map<string, string[]>();
    for (const row of this._asArray(this._frame?.engagements)) {
      const id = row.engagement_id;
      const a = out.get(row.unit_a_id) || [];
      a.push(id);
      out.set(row.unit_a_id, a);
      const b = out.get(row.unit_b_id) || [];
      b.push(id);
      out.set(row.unit_b_id, b);
    }
    return out;
  }

  private _skillsByUnit(): Map<string, string[]> {
    const grouped = new Map<string, { ordinal: number; skill: string }[]>();
    for (const row of this._asArray(this._frame?.unit_skills)) {
      const list = grouped.get(row.unit_id) || [];
      list.push({ ordinal: row.ordinal, skill: row.skill_name });
      grouped.set(row.unit_id, list);
    }
    const out = new Map<string, string[]>();
    for (const [unitId, skills] of grouped.entries()) {
      skills.sort((l, r) => l.ordinal - r.ordinal);
      out.set(unitId, skills.map((e) => e.skill));
    }
    return out;
  }

  private _orderQueueByUnit(): Map<string, any[]> {
    const grouped = new Map<string, any[]>();
    for (const row of this._asArray(this._frame?.unit_order_queue)) {
      const list = grouped.get(row.unit_id) || [];
      list.push({
        ordinal: row.ordinal,
        order_type: row.order_type,
        target_x: row.target_x ?? null,
        target_y: row.target_y ?? null,
        facing: row.facing ?? null,
        target_unit_id: row.target_unit_id ?? null,
      });
      grouped.set(row.unit_id, list);
    }
    for (const list of grouped.values()) list.sort((l, r) => l.ordinal - r.ordinal);
    return grouped;
  }

  private _pathWaypointsByUnit(): Map<string, [number, number][]> {
    const grouped = new Map<
      string,
      { ordinal: number; point: [number, number] }[]
    >();
    for (const row of this._asArray(this._frame?.unit_path_waypoints)) {
      const list = grouped.get(row.unit_id) || [];
      list.push({ ordinal: row.ordinal, point: [row.x, row.y] });
      grouped.set(row.unit_id, list);
    }
    const out = new Map<string, [number, number][]>();
    for (const [unitId, waypoints] of grouped.entries()) {
      waypoints.sort((l, r) => l.ordinal - r.ordinal);
      out.set(unitId, waypoints.map((e) => e.point));
    }
    return out;
  }
}
